import { Component, inject, Inject } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, startWith, map, of } from 'rxjs';
import { formatColumnName, rangeValidator } from 'src/app/modules/Disbursment_modules/master-tabs/pages/tariff-services/tariff-service.model';
import { DisbursmentService } from 'src/app/shared/service/disbursment/disbursment.service';
import { PortService } from 'src/app/shared/service/disbursment/port.service';
import { MaCountry } from '../../create-port-tariff/country.model';
import { MaPort } from '../../create-port-tariff/port.model';
import { ToastrService } from 'ngx-toastr';
import { CountryService } from 'src/app/shared/service/disbursment/country.service';
import { TariffService } from 'src/app/shared/service/disbursment/tariff-services.service';

@Component({
  selector: 'app-create-service-data',
  templateUrl: './create-service-data.component.html',
  styleUrls: ['./create-service-data.component.scss']
})
export class CreateServiceDataComponent {
 private loader = inject(DisbursmentService);
 isLoading$ = this.loader.loading$;
  formatColumnName = formatColumnName;
  serviceForm!: FormGroup;
  isEditMode = false;
  countryData: MaCountry[] = [];
  portData: MaPort[] = [];
  isSubmitted = false;
  rows: FormGroup[] = [];
  filteredCountries: { [key: string]: Observable<MaCountry[]> } = {};
  filteredPorts: { [key: string]: Observable<MaPort[]> } = {};

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateServiceDataComponent>,
    @Inject(MAT_DIALOG_DATA) public existingdata: any,
    private countryService: CountryService,
    private portService: PortService,
    private snackBar: ToastrService,
    private tariffService: TariffService,
  ) {
    const data = existingdata?.rowData;
    this.isEditMode = !!data;
    this.getMaCountryDataFromDb();
    this.buildForm();
  }

  buildForm(): void {
    const data = this.existingdata?.rowData;
    this.serviceForm = this.fb.group({
      rows: this.fb.array([])
    });

    if (this.isEditMode) {
      this.addRow(data);
    } else {
      this.addRow();
    }
  }

  get rowsArray(): FormArray {
    return this.serviceForm.get('rows') as FormArray;
  }

  addRow(data?: any): void {
    if (!this.isEditMode && this.rowsArray.length > 0 && !this.isLastRowValid()) {
      this.snackBar.error('Please fill all required fields in the current row before adding a new row.');
      return;
    }

    const rowGroup = this.fb.group({
      seq: [data?.seq || null],
      fields: this.fb.array([], rangeValidator)
    });

    // Only process if existingdata has fields
    if (this.existingdata?.fields?.length) {
      // Track previous row values to prefill Country/Port
      const previousRowValues = (!this.isEditMode && !data) ? this.getPreviousRowValues() : null;

      this.existingdata.fields.forEach((field: any, fieldIndex: number) => {
        const actualFieldKey = formatColumnName(field.field_name);
        let dynamicValue = data?.[actualFieldKey] ?? '';

        // Convert ID to object for Country and Port in edit mode
        if (this.isEditMode && data && dynamicValue) {
          if (field.field_name.toLowerCase() === 'country') {
            const country = this.countryData.find(c => c.country_id === dynamicValue);
            dynamicValue = country || dynamicValue;
            if (country?.country_id) this.loadPortsForCountry(country.country_id);
          } else if (field.field_name.toLowerCase() === 'port') {
            const port = this.portData.find(p => p.port_id === dynamicValue);
            dynamicValue = port || dynamicValue;
          }
        }

        // Pre-fill Country and Port from previous row in create mode
        if (!this.isEditMode && !data && previousRowValues) {
          if (field.field_name.toLowerCase() === 'country' && previousRowValues.country) {
            dynamicValue = previousRowValues.country;
          } else if (field.field_name.toLowerCase() === 'port' && previousRowValues.port) {
            dynamicValue = previousRowValues.port;
          }
        }

        // Build the field FormGroup
        const fieldsArray = rowGroup.get('fields') as FormArray;
        const fieldGroup = this.fb.group({
          field_name: [field.field_name],
          data_type: [field.data_type],
          value: [
            dynamicValue,
            field.is_mandatory === 'Y' ? Validators.required : []
          ]
        });

        fieldsArray.push(fieldGroup);

        // Setup autocomplete for Country/Port
        if (field.field_name.toLowerCase() === 'country') {
          this.setupCountryAutocomplete(fieldGroup.get('value')!, `${this.rowsArray.length}-${fieldIndex}`);
        } else if (field.field_name.toLowerCase() === 'port') {
          this.setupPortAutocomplete(fieldGroup.get('value')!, `${this.rowsArray.length}-${fieldIndex}`);
        }
      });
    }

    // Finally, add the row to the FormArray
    this.rowsArray.push(rowGroup);
  }

  private loadPortsForCountry(countryId: number): void {
    this.portService.portsByCountry(countryId).subscribe({
      next: (ports: MaPort[]) => {
        this.portData = ports;
      },
      error: (error) => {
        this.portData = [];
      }
    });
  }

  private isLastRowValid(): boolean {
    const lastRowIndex = this.rowsArray.length - 1;
    const lastRow = this.rowsArray.at(lastRowIndex);
    const fields = lastRow.get('fields') as FormArray;

    for (let i = 0; i < fields.length; i++) {
      const fieldGroup = fields.at(i);
      const fieldName = fieldGroup.get('field_name')?.value;
      const isRequired = this.existingdata?.fields?.find((f: any) => f.field_name === fieldName)?.is_mandatory === 'Y';
      const value = fieldGroup.get('value')?.value;

      if (isRequired && (!value || value === '')) {
        return false;
      }
    }
    return true;
  }

  private getPreviousRowValues(): { country: any, port: any } | null {
    if (this.rowsArray.length === 0) return null;

    const lastRowIndex = this.rowsArray.length - 1;
    const lastRow = this.rowsArray.at(lastRowIndex);
    const fields = lastRow.get('fields') as FormArray;

    let country = null;
    let port = null;

    for (let i = 0; i < fields.length; i++) {
      const fieldGroup = fields.at(i);
      const fieldName = fieldGroup.get('field_name')?.value;
      const value = fieldGroup.get('value')?.value;

      if (fieldName === 'Country' && value) {
        country = value;
      } else if (fieldName === 'Port' && value) {
        port = value;
      }
    }

    return { country, port };
  }

  removeRow(index: number): void {
    if (this.rowsArray.length > 1) {
      this.rowsArray.removeAt(index);
    }
  }


  getFields(rowIndex: number): FormArray {
    return this.rowsArray.at(rowIndex).get('fields') as FormArray;
  }

  // method to set the number type
  isNumericField(dataType: string): boolean {
    return ['decimal', 'number', 'float'].includes(dataType?.toLowerCase());
  }

  ngOnInit() {
    this.getMaCountryDataFromDb();
  }

  private getCountryControl(fieldIndex: number): any {
    for (let rowIndex = 0; rowIndex < this.rowsArray.length; rowIndex++) {
      const fields = this.getFields(rowIndex);
      let currentFieldIndex = 0;
      for (let i = 0; i < fields.length; i++) {
        const fieldGroup = fields.at(i);
        const fieldName = fieldGroup.get('field_name')?.value;
        if (fieldName === 'Country') {
          if (currentFieldIndex === fieldIndex) {
            return fieldGroup.get('value');
          }
          currentFieldIndex++;
        }
      }
    }
    return null;
  }

  private getPortControl(fieldIndex: number): any {
    for (let rowIndex = 0; rowIndex < this.rowsArray.length; rowIndex++) {
      const fields = this.getFields(rowIndex);
      let currentFieldIndex = 0;
      for (let i = 0; i < fields.length; i++) {
        const fieldGroup = fields.at(i);
        const fieldName = fieldGroup.get('field_name')?.value;
        if (fieldName === 'Port') {
          if (currentFieldIndex === fieldIndex) {
            return fieldGroup.get('value');
          }
          currentFieldIndex++;
        }
      }
    }
    return null;
  }

  setupCountryAutocomplete(control: any, index: string | number): void {
    this.filteredCountries[index] = control.valueChanges.pipe(
      startWith(control.value || ''),
      map((value: string | MaCountry) => this._filterCountries(value))
    );
  }

  setupPortAutocomplete(control: any, index: string | number): void {
    this.filteredPorts[index] = control.valueChanges.pipe(
      startWith(control.value || ''),
      map((value: string | MaPort) => this._filterPorts(value))
    );
  }

  private _filterCountries(value: string | MaCountry): MaCountry[] {
    if (!this.countryData.length) return [];

    const filterValue = typeof value === 'string' ? value.toLowerCase() : (value?.name || '').toLowerCase();

    if (!filterValue) {
      return this.countryData.slice(0, 50); // Show first 50 countries when empty
    }

    return this.countryData.filter(country =>
      country.name?.toLowerCase().includes(filterValue)
    );
  }

  private _filterPorts(value: string | MaPort): MaPort[] {
    if (!this.portData.length) return [];

    const filterValue = typeof value === 'string' ? value.toLowerCase() : (value?.name || '').toLowerCase();

    if (!filterValue) {
      return this.portData.slice(0, 50); // Show first 50 ports when empty
    }

    return this.portData.filter(port =>
      port.name?.toLowerCase().includes(filterValue)
    );
  }

  onCountrySelect(country: MaCountry): void {
    if (!country?.country_id) return;

    // Only show loader if not already loading
    if (!this.isLoading$) {
      this.loader.showDialogLoader();
    }

    this.portService.portsByCountry(country.country_id).subscribe({
      next: (ports: MaPort[]) => {
        this.portData = ports;
        this.loader.hideDialogLoader();
      },
      error: (error) => {
        this.portData = [];
        this.loader.hideDialogLoader();
        const errMsg = error?.error?.error;
        this.snackBar.error(errMsg);
      }
    });
  }

  displayCountry(country: MaCountry | null): string {
    return country?.name || '';
  }

  displayPort(port: MaPort | null): string {
    return port?.name || '';
  }

  onCountryFocus(rowIndex: number, fieldIndex: number): void {
    const key = `${rowIndex}-${fieldIndex}`;
    if (!this.filteredCountries[key]) {
      const control = this.getFieldControl(rowIndex, fieldIndex, 'Country');
      if (control) {
        this.setupCountryAutocomplete(control, key);
      }
    }
  }

  

  onPortFocus(rowIndex: number, fieldIndex: number): void {
    const key = `${rowIndex}-${fieldIndex}`;
    if (!this.filteredPorts[key]) {
      const control = this.getFieldControl(rowIndex, fieldIndex, 'Port');
      if (control) {
        this.setupPortAutocomplete(control, key);
      }
    }
  }

  getCountryOptions(rowIndex: number, fieldIndex: number): Observable<MaCountry[]> {
    const key = `${rowIndex}-${fieldIndex}`;
    return this.filteredCountries[key] || of([]);
  }

  getPortOptions(rowIndex: number, fieldIndex: number): Observable<MaPort[]> {
    const key = `${rowIndex}-${fieldIndex}`;
    return this.filteredPorts[key] || of([]);
  }

  private getFieldControl(rowIndex: number, fieldIndex: number, fieldName: string): any {
    const fields = this.getFields(rowIndex);
    const fieldGroup = fields.at(fieldIndex);
    if (fieldGroup?.get('field_name')?.value === fieldName) {
      return fieldGroup.get('value');
    }
    return null;
  }
  // method to format the payload to create or edit the service
  formatPayload(rowValue: any) {
    const payload: any = {};

    rowValue.fields.forEach((field: any) => {
      if (field.field_name && field.value !== undefined) {
        const key = formatColumnName(field.field_name);
        const isNumeric = ['number', 'decimal'].includes(field.data_type?.toLowerCase());

        if (field.value === '') {
          payload[key] = null;
        } else if (field.field_name.toLowerCase() === 'country' && typeof field.value === 'object') {
          payload[key] = field.value.country_id;
        } else if (field.field_name.toLowerCase() === 'port' && typeof field.value === 'object') {
          payload[key] = field.value.port_id;
        } else {
          payload[key] = isNumeric ? Number(field.value) : field.value;
        }
      }
    });

    if (rowValue.seq !== null && rowValue.seq !== undefined) {
      payload['seq'] = rowValue.seq || null;
    }

    return payload;
  }

  // onSubmit method will trigger when the Add or Edit  button clicks
  onSubmit() {
    this.isSubmitted = true;
    const formValue = this.serviceForm.value;

    if (this.serviceForm.valid) {
      this.loader.showDialogLoader();

      const payloads = formValue.rows.map((row: any) => this.formatPayload(row));
      console.log("payloads", payloads);
      this.tariffService.createOrUpdateServiceData(this.existingdata.serviceName, payloads).subscribe({
        next: (res) => {
          this.loader.hideDialogLoader();
          const message = this.isEditMode ? 'Record updated successfully' : `${payloads.length} records added successfully`;
          this.snackBar.success(message);
          this.dialogRef.close("refresh");
        },
        error: (error) => {
          this.loader.hideDialogLoader();
          const errorMsg = error.error?.error || 'Something went wrong. Please try again.';
          this.snackBar.error(errorMsg);
        }
      });
    }
  }

  // method to get the country list
  getMaCountryDataFromDb() {
    const body = {
      fields: [
        "country"
      ]
    }
    this.loader.showDialogLoader();
    this.countryService.MaCountryDataFromDb(body).subscribe({
      next: (response: any) => {
        this.countryData = response;
        this.loader.hideDialogLoader();

        // If in edit mode, update the form values after country data is loaded
        if (this.isEditMode && this.rowsArray.length > 0) {
          this.updateEditModeValues();
        }
      },
      error: (error) => {
        this.loader.hideDialogLoader();
        const errorMsg = error.error?.error || 'Something went wrong. Please try again.';
        this.snackBar.error(errorMsg);
      }
    });
  }

  private updateEditModeValues(): void {
    const fields = this.getFields(0);
    let countryId: number | null = null;
    let portId: number | null = null;

    // First pass: find country and port IDs
    for (let i = 0; i < fields.length; i++) {
      const fieldGroup = fields.at(i);
      const fieldName = fieldGroup.get('field_name')?.value;
      const currentValue = fieldGroup.get('value')?.value;

      if (fieldName.toLowerCase() === 'country' && typeof currentValue === 'number') {
        countryId = currentValue;
      } else if (fieldName.toLowerCase() === 'port' && typeof currentValue === 'number') {
        portId = currentValue;
      }
    }

    // Second pass: update country and load ports
    for (let i = 0; i < fields.length; i++) {
      const fieldGroup = fields.at(i);
      const fieldName = fieldGroup.get('field_name')?.value;
      const currentValue = fieldGroup.get('value')?.value;

      if (fieldName.toLowerCase() === 'country' && typeof currentValue === 'number') {
        const country = this.countryData.find(c => c.country_id === currentValue);
        if (country && country.country_id) {
          fieldGroup.get('value')?.setValue(country);
          if (portId) {
            this.loadPortsForCountryAndSetPort(country.country_id, portId);
          } else {
            this.loadPortsForCountry(country.country_id);
          }
        }
      }
    }
  }

  private loadPortsForCountryAndSetPort(countryId: number, portId: number): void {
    this.portService.portsByCountry(countryId).subscribe({
      next: (ports: MaPort[]) => {
        this.portData = ports;
        // Update port field after ports are loaded
        const fields = this.getFields(0);
        for (let i = 0; i < fields.length; i++) {
          const fieldGroup = fields.at(i);
          const fieldName = fieldGroup.get('field_name')?.value;
          const currentValue = fieldGroup.get('value')?.value;

          if (fieldName.toLowerCase() === 'port' && typeof currentValue === 'number') {
            const port = this.portData.find(p => p.port_id === portId);
            if (port) {
              fieldGroup.get('value')?.setValue(port);
            }
          }
        }
      },
      error: (error) => {
        this.portData = [];
      }
    });
  }

  // method to close the dialog
  closeDialog() {
    this.dialogRef.close();
  }
}
